import { Injectable } from '@angular/core';
import { of, Observable, throwError } from 'rxjs';
import { switchMap, map, shareReplay, catchError } from 'rxjs/operators';
import { User, UserPreferences, UserPreferencesInput, UserVerificationDetails, ConfirmIpAddressResponse, CustomerSupportContactDetails } from '../models/user';
import { ApiService } from './api.service';
import { CreateTotpResponse, VerifyTotpResponse, TotpDevices } from '../models/totp-device';
import { OtpOption, SendOtpResponse } from '../models/otp-option';
import { PhoneInput } from '../models/phone';
import _keyBy from 'lodash-es/keyBy';
import { UserAccess } from '../models/user-access';
import { AuthService, AuthStatus } from './auth.service';
import { LogService } from '../services/log.service';
import { UiGuideViewStatus, UiGuide } from './ui-guide';
import { EmailSubscription } from '../models/subscription';
import { CreditCard } from '../models/credit-card';

declare var _mfq: any;

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly REQUEST_SPAN = 1000 * 60 * 5;

  private generalQuery = `
    firstName
    lastName
    email
    isEmailConfirmed
    isPhoneConfirmed
    hasWallet
    multisigBtcWalletEnabled
    exchangeTermsAndConditionsAgreed
    vip
  `;

  private phoneQuery = `phone{
    code{
      id
      code
      name
      callSupported
      country{
        id
        name
        code
        code3
        gcc
      }
    }
    censoredPhoneNumber
  }`;

  private addressQuery = `addresses{
    addressLine1
    city
    country{
      id,
      name,
      code,
      code3,
      gcc
    }
    zip
  }`;

  private preferencesQuery = `preferences{
    fiatCurrencyCode
    language
    timeZone{
      name
      utcOffsetSec
    }
    totpFallbackDisabled
  }`;

  private totpQuery = `totpDeviceList{
    id
    name
    dateAuthorized
  }`;

  private verificationDetailsQuery = `verificationDetails{
    tier
    missingVerificationFields
  }`;

  private creditCardsQuery = `creditCards{
    id
    brand
    censoredNumber
    expiryYear
    expiryMonth
    holderName
  }`;

  private uiGuidesQuery = `uiGuideViewStatuses{
    guide
    viewed
    dateViewed
  }`;

  private contactDetailsQuery = `customerSupportContactDetails{
    name
    email
    phone
    imageName
  }`;

  private emailSubscriptionsQuery = `emailSubscriptionTypes{
    id
  }`;

  getRequest: Observable<User>;
  accessRequest: Observable<UserAccess[]>;
  timestamp: number;

  private verifyIpResponse: ConfirmIpAddressResponse;

  constructor(private api: ApiService, private auth: AuthService, private logService: LogService) {
    this.auth.statusChanges.subscribe(status => {
      if (status === AuthStatus.LoggedOff) {
        this.clearCache();
      }
    });
  }

  get(forceRefresh = false): Observable<User> {
    const now = new Date().getTime();
    if (forceRefresh || !this.getRequest || now - this.timestamp > this.REQUEST_SPAN) {
      this.timestamp = now;
      this.getRequest = this.api.get(`{
          profile{
            ${this.generalQuery}
            ${this.phoneQuery}
            ${this.addressQuery}
            ${this.preferencesQuery}
            ${this.verificationDetailsQuery}
            ${this.creditCardsQuery}
            ${this.uiGuidesQuery}
            ${this.contactDetailsQuery}
          }
        }`).pipe(
        switchMap<any, Observable<User>>(response => {
          const profile = response['data']['profile'];
          if (response['errors'] || profile['errors']) {
            return throwError(profile);
          }

          // Register user identity in mouseflow
          try { _mfq.push(['identify', profile.email]); } catch (err) { }

          // Register user with LogSerive
          this.logService.setUser(profile.email);

          return of(profile);
        }),
        this.api.unifyErrors(),
        catchError(err => {
          this.getRequest = null;
          return throwError(err);
        }),
        shareReplay()
      );
    }

    return this.getRequest;
  }

  getGeneral(forceRefresh = false): Observable<User> {
    return this.get(forceRefresh);
  }

  getAccessList(): Observable<UserAccess[]> {
    if (this.accessRequest) {
      return this.accessRequest;
    }

    this.accessRequest = this.api.get(`{
      userAccess {
        key
        state
        reason
        reasonStatus
        disabledFrom
        disabledTo
      }
    }`).pipe(
      switchMap(response => {
        const userAccess = response['data']['userAccess'];

        if (response['errors']) {
          return throwError(response);
        }

        return of(_keyBy(userAccess, 'key'));
      }),
      this.api.unifyErrors(),
      catchError(err => {
        this.accessRequest = null;
        return throwError(err);
      }),
      shareReplay()
    );

    return this.accessRequest;
  }

  getPreferences(): Observable<UserPreferences> {
    return this.get().pipe(
      map(response => {
        return response.preferences;
      })
    );
  }

  getVerificationDetails(): Observable<UserVerificationDetails> {
    return this.get().pipe(
      map(response => {
        return response.verificationDetails;
      })
    );
  }

  getCreditCards(): Observable<CreditCard[]> {
    return this.get().pipe(
      map(response => {
        return response.creditCards;
      })
    );
  }

  getUiGuideStatuses(): Observable<UiGuideViewStatus[]> {
    return this.get().pipe(
      map(response => {
        return response.uiGuideViewStatuses;
      })
    );
  }

  getContactDetails(): Observable<CustomerSupportContactDetails> {
    return this.get().pipe(
      map(response => {
        return response.customerSupportContactDetails;
      })
    );
  }

  registerUser(user: User, referralId: string): Observable<User> {
    return this.api.get(`mutation{
      registerUser(
        user: {
          firstName: "${user.firstName}"
          lastName: "${user.lastName}"
          email: "${user.email}"
          password: "${user.password}"
          referralId: "${referralId}"
        }
      ){
          ... on User{
            ${this.generalQuery}
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
      }
    }`, null, false).pipe(
      switchMap(response => {
        const data = response['data']['registerUser'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  confirmRegisteredEmail(code: string): Observable<boolean> {
    return this.api.get(`mutation{
      confirmRegisteredEmail(code: "${code}"){
          ... on GeneralResponse{
            success
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['confirmRegisteredEmail'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  resendEmailRegistrationCode(email: string): Observable<boolean> {
    return this.api.get(`mutation{
      resendEmailRegistrationCode(email: "${email}"){
          ... on GeneralResponse{
            success
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['resendEmailRegistrationCode'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  registerPhone(phone: PhoneInput): Observable<boolean> {
    return this.api.get(`mutation{
      registerPhone(phone: {
        codeId: ${phone.codeId},
        number: "${phone.number}"
      }){
          ... on GeneralResponse{
            success
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['registerPhone'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  confirmRegisteredPhone(code: string): Observable<boolean> {
    return this.api.get(`mutation{
      confirmRegisteredPhone(code: "${code}"){
          ... on GeneralResponse{
            success
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['confirmRegisteredPhone'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  resendPhoneRegistrationCode(): Observable<boolean> {
    return this.api.get(`mutation{
      resendPhoneRegistrationCode{
          ... on GeneralResponse{
            success
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['resendPhoneRegistrationCode'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  updatePreferences(preferences: UserPreferencesInput): Observable<UserPreferences> {
    return this.api.get(`mutation updateProfilePreferences($preferences: ProfilePreferencesInput!) {
      updateProfilePreferences(
        preferences: $preferences
      ){
        ... on ProfilePreferences {
            fiatCurrencyCode
            language
            timeZone{
              name
              utcOffsetSec
            }
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      preferences
    }).pipe(
      switchMap(response => {
        const data = response['data']['updateProfilePreferences'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  totpDeviceList(): Observable<TotpDevices> {
    return this.api.get(`{
      profile{
        ${this.totpQuery}
        preferences{
          totpFallbackDisabled
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        const data = response['data']['profile'];
        return of({
          list: data['totpDeviceList'],
          fallbackDisabled: data['preferences'] ? data['preferences']['totpFallbackDisabled'] : false
        });
      }),
      this.api.unifyErrors()
    );
  }

  createTotpDevice(): Observable<CreateTotpResponse> {
    return this.api.get(`mutation{
      createTotpDevice{
        ... on CreateTotpDevice{
          id
          otpOptions
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['createTotpDevice'];
        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  sendTotpDeviceSetupVerificationCode(id: string, otpOption: OtpOption): Observable<SendOtpResponse> {
    return this.api.get(`mutation{
      sendTotpDeviceSetupVerificationCode(
        id: ${id},
        option: ${otpOption}
      ){
        ... on SendOtpResponse{
          success
          maskedPhoneNumber
          option
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['sendTotpDeviceSetupVerificationCode'];
        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  verifyTotpDeviceSetup(id: string, code: string): Observable<VerifyTotpResponse> {
    return this.api.get(`mutation{
      verifyTotpDeviceSetup(
        id: ${id},
        code: "${code}"
      ){
        ... on VerifyTotpDeviceSetup{
          id
          secret
          qrCode
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['verifyTotpDeviceSetup'];
        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  finishTotpDeviceSetup(id: string, deviceName: string, code: string, disableFallback = false): Observable<boolean> {
    return this.api.get(`mutation finishTotpDeviceSetup($id: ID!, $deviceName: String!, $code: String!, $disableFallback: Boolean){
      finishTotpDeviceSetup(
        id: $id,
        deviceName: $deviceName,
        code: $code
        disableFallback: $disableFallback
      ){
        ... on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id,
      deviceName,
      code,
      disableFallback
    }).pipe(
      switchMap(response => {
        const data = response['data']['finishTotpDeviceSetup'];
        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  changeTotpFallback(code: string, state: boolean): Observable<boolean> {
    return this.api.get(`mutation changeTotpFallback($code: String!, $state: Boolean!){
      changeTotpFallback(
        code: $code
        state: $state
      ){
        ... on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      code,
      state
    }).pipe(
      switchMap(response => {
        const data = response['data']['changeTotpFallback'];
        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  getTotpDeviceRemovalOtpOptions(): Observable<OtpOption[]> {
    return this.api.get(`{
      totpDeviceRemovalOtpOptions{
        options
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['totpDeviceRemovalOtpOptions']['options']);
      }),
      this.api.unifyErrors()
    );
  }

  sendTotpDeviceRemovalVerificationCode(id: string, otpOption: OtpOption): Observable<SendOtpResponse> {
    return this.api.get(`mutation{
      sendTotpDeviceRemovalVerificationCode(
        id: ${id},
        option: ${otpOption}
      ){
        ... on SendOtpResponse{
          success
          maskedPhoneNumber
          option
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['sendTotpDeviceRemovalVerificationCode'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  removeTotpDevice(id: string, code: string): Observable<boolean> {
    return this.api.get(`mutation{
      removeTotpDevice(
        id: ${id},
        code: "${code}"
      ){
        ... on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['removeTotpDevice'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  removeCard(id: number): Observable<boolean> {
    return this.api.get(`mutation{
      removeCreditCard(
        id: ${id}
      ){
        ... on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['removeCreditCard'];

        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  agreeExchangeTermsAndConditions(): Observable<boolean> {
    return this.api.get(`mutation{
      agreeExchangeTermsAndConditions{
        ... on GeneralResponse{
            success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['agreeExchangeTermsAndConditions'];
        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  unsubscribeFromEmails(id: string, token: string, type: string): Observable<boolean> {
    return this.api.get(`mutation{
      unsubscribeFromEmails(
        userId: ${id},
        token: "${token}",
        type: ${type}
      ){
        ... on GeneralResponse{
            success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, null, false).pipe(
      switchMap(response => {
        const data = response['data']['unsubscribeFromEmails'];
        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  verifyIp(email: string, token: string): Observable<ConfirmIpAddressResponse> {
    return this.api.get(`mutation{
      confirmIpAddress(
        email: "${email}",
        token: "${token}"
      ){
        ... on ConfirmIpAddressResponse{
            key
            message
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, null, false).pipe(
      switchMap(response => {
        const data = response['data']['confirmIpAddress'];
        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        this.verifyIpResponse = data;
        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  getIpVerificationResponse() {
    const response = this.verifyIpResponse;
    this.verifyIpResponse = null;
    return response;
  }

  updateUiGuideStatus(guide: UiGuide, viewed: boolean): Observable<boolean> {
    return this.api.get(`mutation updateUiGuideViewStatus($guide: UiGuide!, $viewed: Boolean!) {
      updateUiGuideViewStatus(
        guide: $guide,
        viewed: $viewed
      ){
        ... on GeneralResponse{
            success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      guide,
      viewed
    }).pipe(
      switchMap(response => {
        const data = response['data']['updateUiGuideViewStatus'];
        if (data['errors']) {
          return throwError(data);
        }

        this.clearCache();
        this.verifyIpResponse = data;
        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  emailSubscriptions(): Observable<EmailSubscription[]> {
    return this.api.get(`{
      profile{
        emailSubscriptionTypes{
          id
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['profile']['emailSubscriptionTypes']);
      }),
      this.api.unifyErrors()
    );
  }

  clearCache() {
    this.getRequest = null;
    this.accessRequest = null;
  }
}
